import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { LogOut } from 'lucide-react'
import { useOwner } from '../contexts/OwnerContext'
import ConfirmModal from './ConfirmModal'
import { clearAllCaches, validateCachesClear } from '../utils/cacheManager'
import { resetReduxStore } from '../store'

const LogoutButton: React.FC = () => {
  const { clearOwner, isOwnerValid, headers } = useOwner()
  const [showConfirmModal, setShowConfirmModal] = useState(false)

  const handleLogoutClick = () => {
    setShowConfirmModal(true)
  }

  const handleConfirmLogout = async () => {
    try {
      // Step 1: Call backend logout endpoint to clear any server-side cache (with timeout)
      const fetchWithTimeout = (url: string, options: RequestInit, timeout = 3000) => {
        return Promise.race([
          fetch(url, options),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), timeout))
        ])
      }

      try {
        await fetchWithTimeout('/api/v1/logout/clear-cache', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'company': headers?.company || '',
            'business_unit': headers?.businessUnit || '',
            'logistic_operator': headers?.logisticOperator || '',
            'authorization': headers?.userToken || '',
          },
        })
      } catch (error) {
        console.warn('[LogoutButton] Backend cache clear failed, continuing with client-side cleanup')
      }

      // Step 2: Clear owner data using context
      clearOwner()

      // Step 3: Reset Redux store (clears RTK Query cache)
      resetReduxStore()
      console.log('[LogoutButton] Redux store reset')

      // Step 4: Clear all client-side caches using centralized utility
      clearAllCaches()

      // Step 5: Validate that caches were cleared (for debugging)
      const validation = validateCachesClear()
      if (!validation.cleared) {
        console.warn('[LogoutButton] Some caches remain after clearing:', validation.remainingCaches)
      } else {
        console.log('[LogoutButton] All caches successfully cleared')
      }

      // Step 6: Clear any cookies
      document.cookie.split(';').forEach((cookie) => {
        const eqPos = cookie.indexOf('=')
        const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim()
        if (name) {
          document.cookie = `${name}=;expires=${new Date(0).toUTCString()};path=/`
          document.cookie = `${name}=;expires=${new Date(0).toUTCString()};path=/;domain=${window.location.hostname}`
          document.cookie = `${name}=;expires=${new Date(0).toUTCString()};path=/;domain=.${window.location.hostname}`
        }
      })
      console.log('[LogoutButton] Cookies cleared')

      // Step 7: Clear IndexedDB (non-blocking)
      if ('indexedDB' in window) {
        indexedDB.databases().then(databases => {
          for (const db of databases) {
            if (db.name) {
              indexedDB.deleteDatabase(db.name)
            }
          }
          console.log('[LogoutButton] IndexedDB cleared')
        }).catch(error => {
          console.warn('[LogoutButton] IndexedDB clear failed:', error)
        })
      }

      // Step 8: Clear Service Worker caches (non-blocking)
      if ('caches' in window) {
        caches.keys().then(cacheNames => {
          for (const name of cacheNames) {
            caches.delete(name)
          }
          console.log('[LogoutButton] Service Worker cache cleared')
        }).catch(error => {
          console.warn('[LogoutButton] Service Worker cache clear failed:', error)
        })
      }

      // Step 9: Validate logout with backend (optional, non-blocking)
      fetchWithTimeout('/api/v1/logout/validate-logout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      }).then(() => {
        console.log('[LogoutButton] Logout validated with backend')
      }).catch(() => {
        console.warn('[LogoutButton] Backend logout validation failed')
      })

      // Step 10: Close the modal
      setShowConfirmModal(false)

      // Step 11: Try to close the browser window (works for popups opened via window.open)
      // Check if we can close the window
      const canClose = window.opener !== null || window.history.length <= 1

      if (canClose) {
        window.close()
      }

      // Step 12: Fallback - reload the page to trigger auth redirect
      // This will show the login/auth screen since owner data was cleared
      setTimeout(() => {
        // If window.close() didn't work, reload the page
        // The cleared owner data will trigger the auth flow
        window.location.reload()
      }, 100)

    } catch (error) {
      console.error('[LogoutButton] Logout error:', error)
      // Continue logout despite errors
      setShowConfirmModal(false)

      // Clear owner data even if other steps failed
      try {
        clearOwner()
        resetReduxStore()
        clearAllCaches()
      } catch (e) {
        console.error('[LogoutButton] Emergency cleanup failed:', e)
      }

      // Fallback: reload to trigger auth redirect
      window.location.reload()
    }
  }

  const handleCancelLogout = () => {
    setShowConfirmModal(false)
  }

  // Don't render if owner is not valid
  if (!isOwnerValid) {
    return null
  }

  return (
    <>
      <motion.button
        onClick={handleLogoutClick}
        className="relative p-3 rounded-xl bg-red-500/20 hover:bg-red-500/30 transition-all duration-300 group"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        style={{
          border: '1px solid rgba(239, 68, 68, 0.3)'
        }}
      >
        <motion.div
          className="relative w-6 h-6"
          whileHover={{ rotate: 15 }}
          transition={{ duration: 0.2 }}
        >
          <LogOut 
            className="w-6 h-6" 
            style={{ color: '#ef4444' }}
          />
        </motion.div>
        
        {/* Tooltip */}
        <div 
          className="absolute -top-12 left-1/2 transform -translate-x-1/2 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap"
          style={{
            backgroundColor: 'var(--bg-card)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-primary)'
          }}
        >
          Sair do Sistema
        </div>
      </motion.button>

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={handleCancelLogout}
        onConfirm={handleConfirmLogout}
        title="Confirmar Saída"
        message={`Tem certeza que deseja sair do sistema Lognex Insight? A janela será fechada.`}
        confirmText="Sair do Sistema"
        cancelText="Cancelar"
        type="danger"
      />
    </>
  )
}

export default LogoutButton